import { PublicCallback } from 'globals/types';
import { handleError } from 'globals/errors';
import { importAndInitializePubNub } from 'core_main/globals/vendors';
import { getStore } from 'core_main/store';
import { createSession } from 'core_main/api/sessions';
import { ON_CONVERSATION_CREATE_EVENT_TYPE } from 'core/constants';
import { runListenersForEvent } from 'core_main/globals/events';
import {
  createConversationCallbackResponse,
  createNewConversationStatusEventListener,
} from './helpers';
import { CreateConversationParameters } from './types';
import { ConversationCallbackResponse } from '../types';

/** Wait for conversation-group Connected after session fills an empty PubNub group.
 * Must cover EMPTY_CHANNEL_GROUP_BACKOFF_MS ([300, 800, 2000]) plus subscribe RTT. */
const POST_SESSION_PUBNUB_CONNECT_TIMEOUT_MS = 8000;
const POST_SESSION_PUBNUB_CONNECT_POLL_MS = 50;

const waitForPubNubConnected = async (timeoutMs: number): Promise<boolean> => {
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    if (getStore().isPubNubConnected) {
      return true;
    }
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => {
      setTimeout(resolve, POST_SESSION_PUBNUB_CONNECT_POLL_MS);
    });
  }
  return !!getStore().isPubNubConnected;
};

export const createConversation = (
  parameters: CreateConversationParameters,
  cb?: PublicCallback,
) => {
  return createSession(parameters)
    .then((response) => {
      const customerId = response.data.relationships.customer.data.id;
      const { orgId } = getStore();
      return importAndInitializePubNub({
        customerId,
        orgId,
      }).then((pubnub) => ({ pubnub, response, customerId, orgId }));
    })
    .then(async ({ pubnub, response, customerId, orgId }) => {
      // Session create adds the convo channel to the customer group, but PubNub's
      // subscribe edge can still report empty for a few hundred ms. Always
      // resubscribe after session so we recover from the first-conversation 400.
      const { channels } = response?.data?.attributes?.pubnub?.data || {};
      if (channels?.length) {
        pubnub.resubscribeConversationChannelGroup({ customerId, orgId });
      }

      if (!getStore().isPubNubConnected) {
        const connected = await waitForPubNubConnected(
          POST_SESSION_PUBNUB_CONNECT_TIMEOUT_MS,
        );
        if (!connected) {
          throw new Error('Failed to connect to Pubnub');
        }
      }

      const sharedConversationChannel = (channels || []).find(
        (channel) => channel.type === 'shared-conversation',
      );

      const sharedConversationChannelName = sharedConversationChannel?.name;

      const conversationChannels = sharedConversationChannelName
        ? [sharedConversationChannelName]
        : undefined;
      if (conversationChannels) {
        const listenerParams = {
          status: createNewConversationStatusEventListener(
            {
              conversationChannels,
            },
            (_, error) => {
              let callbackResponse: ConversationCallbackResponse | null = null;
              if (error) {
                // remove the subscription for channels where subscribe failed (otherwise pubnub will try to subscribe to it on the next call to subscribe)
                pubnub.getMetaPubNub()?.instance?.unsubscribe({
                  channels: conversationChannels,
                });
              } else {
                callbackResponse = createConversationCallbackResponse(response);
              }
              pubnub?.getMetaPubNub()?.instance?.removeListener(listenerParams);
              runListenersForEvent(
                ON_CONVERSATION_CREATE_EVENT_TYPE,
                callbackResponse,
                error,
              );

              if (typeof cb === 'function') {
                cb(callbackResponse, error);
              }
            },
          ),
        };
        pubnub.getMetaPubNub()?.instance?.addListener(listenerParams);
      }
      pubnub.getMetaPubNub()?.instance?.subscribe({
        channels: conversationChannels,
      });
      return pubnub;
    })
    .catch((error) => {
      if (typeof cb === 'function') cb(null, error);
      runListenersForEvent(ON_CONVERSATION_CREATE_EVENT_TYPE, null, error);
      handleError(error, { message: 'Failed to create conversation:' });
    });
};
